/**
 * ESocialManualEditNotice Component
 * Avisa que a ficha editada à mão está diferente do cadastro e oferece refazê-la a partir dele.
 */

import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Tooltip
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { ESocialEvent } from '../../../../types/esocial';
import { esocialService } from '../../../../services/esocialService';
import { useAcao } from '../../../../authorization/useVeredito';

interface ESocialManualEditNoticeProps {
  companyId: string;
  event: ESocialEvent;
  onRefreshed: (event: ESocialEvent) => void;
}

const ESocialManualEditNotice: React.FC<ESocialManualEditNoticeProps> = ({
  companyId,
  event,
  onRefreshed
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const atualizar = useAcao('esocial.events.update', 'Atualizar a partir do cadastro');
  const [outdated, setOutdated] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState<string | null>(null);

  const eventId = event.id.toString();
  const editedAt = event.metadataEditedAt;

  useEffect(() => {
    setOutdated(false);
    setRefreshError(null);
    if (!editedAt) return;

    let active = true;
    esocialService
      .getSourceStatus(companyId, eventId)
      .then((status) => {
        if (active) setOutdated(status.canRefresh && status.edited && status.differsFromSource);
      })
      // Sem a consulta não há como afirmar que a ficha está desatualizada: melhor calar.
      .catch(() => undefined);

    return () => {
      active = false;
    };
  }, [companyId, eventId, editedAt]);

  const handleConfirm = async () => {
    setRefreshing(true);
    setRefreshError(null);
    try {
      const updated = await esocialService.refreshFromSource(companyId, eventId);
      setConfirmOpen(false);
      setOutdated(false);
      enqueueSnackbar('Ficha atualizada a partir do cadastro', { variant: 'success' });
      onRefreshed(updated);
    } catch (err: unknown) {
      setConfirmOpen(false);
      setRefreshError(err instanceof Error ? err.message : 'Não foi possível atualizar a ficha');
    } finally {
      setRefreshing(false);
    }
  };

  if (!outdated) return null;

  return (
    <Box sx={{ mb: 2 }}>
      <Alert
        severity="warning"
        action={
          <Tooltip title={atualizar.tooltip}>
            <span>
              <Button
                color="inherit"
                size="small"
                onClick={() => setConfirmOpen(true)}
                disabled={atualizar.disabled}
              >
                Atualizar a partir do cadastro
              </Button>
            </span>
          </Tooltip>
        }
      >
        Esta ficha foi editada à mão e está diferente do cadastro atual. Ela será enviada como está.
      </Alert>

      {refreshError && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {refreshError}
        </Alert>
      )}

      <Dialog open={confirmOpen} onClose={() => !refreshing && setConfirmOpen(false)}>
        <DialogTitle>Atualizar a ficha a partir do cadastro?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            As alterações feitas à mão nesta ficha serão substituídas pelos dados do cadastro.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={refreshing}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleConfirm}
            disabled={refreshing}
            startIcon={refreshing ? <CircularProgress size={20} /> : null}
          >
            Atualizar ficha
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ESocialManualEditNotice;
